import React, { useState, useEffect } from 'react'
import { ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTransactions } from '@/hooks/useTransactions'
import { useBalance } from '@/hooks/useBalance'
import { useCurrency } from '@/hooks/useCurrency'
import Skeleton from '@/components/common/Skeleton'

interface SendLocationState {
  presetSymbol?: string
  locked?: boolean
}

const Send = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as SendLocationState | null
  const { tokens, loading: balanceLoading } = useBalance()
  const { sendToken, loading: txLoading, error: txError } = useTransactions()
  const { formatValue } = useCurrency()

  const isLocked = Boolean(locationState?.locked && locationState?.presetSymbol)

  const [toAddress, setToAddress] = useState('')
  const [amount, setAmount] = useState('')
  const [selectedToken, setSelectedToken] = useState(
    tokens.find((tk) => tk.symbol === locationState?.presetSymbol) ||
      tokens[0] ||
      null
  )
  const [success, setSuccess] = useState(false)
  const [txHash, setTxHash] = useState('')

  useEffect(() => {
    if (tokens.length > 0 && !selectedToken) {
      const preset = locationState?.presetSymbol
        ? tokens.find((tk) => tk.symbol === locationState.presetSymbol)
        : undefined
      setSelectedToken(preset || tokens[0])
    }
  }, [tokens, selectedToken, locationState])

  const handlePasteAddress = async () => {
    try {
      const text = await navigator.clipboard.readText()
      setToAddress(text.trim())
    } catch (err) {
      console.error('Clipboard read error:', err)
    }
  }

  const amountUSD = parseFloat(amount || '0') * (selectedToken?.priceUSD || 0)

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!toAddress || !amount || !selectedToken) return

    try {
      const result = await sendToken({
        to: toAddress,
        amount: amount,
        tokenAddress: selectedToken.isNative ? undefined : selectedToken.address
      })

      if (result?.hash) {
        setTxHash(result.hash)
        setSuccess(true)
      }
    } catch (err) {
      console.error('Send error:', err)
    }
  }

  if (success) {
    return (
      <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4 items-center">
        <div className="bg-card border border-border-base rounded-3xl p-8 flex flex-col items-center gap-6 shadow-xl w-full">
          <CheckCircle2 className="w-20 h-20 text-accent animate-bounce" />
          <h2 className="text-2xl font-bold text-text-main text-center">
            {t('send.success_title')}
          </h2>
          <p className="text-text-secondary text-center">
            {t('send.success_desc')}
          </p>
          <div className="w-full p-4 bg-text-main/5 rounded-2xl break-all">
            <p className="text-xs text-text-secondary uppercase font-bold mb-1">
              {t('send.hash_label')}
            </p>
            <p className="text-xs font-mono text-text-main">{txHash}</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-gradient-fill w-full py-4 rounded-2xl font-bold mt-4"
          >
            {t('send.back_to_dashboard')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-text-main/5 hover:bg-text-main/10 transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-text-main" />
        </button>
        <h1 className="text-3xl font-bold text-text-main">{t('send.title')}</h1>
      </div>

      <form
        onSubmit={handleSend}
        className="bg-card border border-border-base rounded-3xl p-8 flex flex-col gap-6 shadow-xl"
      >
        {txError && (
          <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-2xl flex items-center gap-3 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm font-medium">{txError}</p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('send.recipient_address')}
          </label>
          <div className="relative">
            <input
              type="text"
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              placeholder="0x..."
              className="w-full p-4 pr-14 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main font-mono"
              required
            />
            <button
              type="button"
              onClick={handlePasteAddress}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold primary-gradient-text"
            >
              {t('send.paste')}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('send.select_asset')}
          </label>
          {balanceLoading ? (
            <Skeleton className="h-14 w-full" />
          ) : isLocked ? (
            <div className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent text-text-main">
              {selectedToken?.symbol} - {t('send.balance_label')}:{' '}
              {selectedToken?.balanceFormatted || '0.00'}
            </div>
          ) : (
            <select
              value={selectedToken?.address || ''}
              onChange={(e) =>
                setSelectedToken(
                  tokens.find((t) => t.address === e.target.value) || tokens[0]
                )
              }
              className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main appearance-none cursor-pointer"
            >
              {tokens.map((token) => (
                <option
                  key={token.address + token.symbol}
                  value={token.address}
                  className="bg-card text-text-main"
                >
                  {token.symbol} - {t('send.balance_label')}: {token.balanceFormatted || '0.00'}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('send.amount')}
          </label>
          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="any"
              className="w-full p-4 pr-28 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main"
              required
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-3">
              <span className="text-xs font-bold text-text-secondary uppercase">
                {selectedToken?.symbol}
              </span>
              <button
                type="button"
                onClick={() =>
                  selectedToken &&
                  setAmount(selectedToken.balanceFormatted || '0')
                }
                className="text-xs font-bold primary-gradient-text"
              >
                {t('send.max')}
              </button>
            </div>
          </div>
          <p className="text-xs text-text-secondary ml-1">
            ≈ {formatValue(amountUSD)}
          </p>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={txLoading || !toAddress || !amount}
            className="btn-gradient-fill w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {txLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              <span>{t('send.submit')}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default Send
